// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {HideIcon, RetryIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import type {MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {openUnlockModal} from '@app/features/vault/commands/VaultModalCommands';
import {VAULT_CODE_HIDDEN, VAULT_CODE_TEMP} from '@app/features/vault/constants';
import Vault from '@app/features/vault/state/Vault';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

const HIDE_CONVERSATION_DESCRIPTOR = msg({
	message: 'Hide conversation',
	comment: 'Context-menu action that moves a DM into the hidden vault section.',
});
const UNHIDE_CONVERSATION_DESCRIPTOR = msg({
	message: 'Unhide conversation',
	comment: 'Context-menu action that moves a hidden DM back to the normal sidebar list.',
});
const MARK_AS_TEMPORARY_DESCRIPTOR = msg({
	message: 'Mark as temporary',
	comment: 'Context-menu action that flags a DM as a temporary vault conversation.',
});
const REMOVE_TEMPORARY_DESCRIPTOR = msg({
	message: 'Remove temporary flag',
	comment: 'Context-menu action that removes the temporary flag from a vault DM.',
});

interface VaultMenuGroupProps {
	channel: Channel;
	onClose: () => void;
	'data-flx'?: string;
}

export const VaultMenuGroup: React.FC<VaultMenuGroupProps> = observer(({channel, onClose, 'data-flx': dataFlx}) => {
	const isHidden = Vault.isInHidden(channel.id);
	const isTemp = Vault.isTempChannel(channel.id);
	const handleToggleHidden = useCallback(() => {
		if (Vault.beginHideChannel(channel.id)) {
			openUnlockModal(VAULT_CODE_HIDDEN);
		}
		onClose();
	}, [channel.id, onClose]);
	const handleToggleTemp = useCallback(() => {
		if (Vault.beginTempMark(channel.id)) {
			openUnlockModal(VAULT_CODE_TEMP);
		}
		onClose();
	}, [channel.id, onClose]);
	return (
		<MenuGroup data-flx={dataFlx ?? 'vault.menu-group'}>
			<MenuItem
				icon={<HideIcon size={20} data-flx="vault.menu-group.hide-icon" />}
				onClick={handleToggleHidden}
				data-flx="vault.menu-group.menu-item.hide"
			>
				{isHidden ? <Trans>Unhide conversation</Trans> : <Trans>Hide conversation</Trans>}
			</MenuItem>
			<MenuItem
				icon={<RetryIcon size={20} data-flx="vault.menu-group.retry-icon" />}
				onClick={handleToggleTemp}
				data-flx="vault.menu-group.menu-item.temporary"
			>
				{isTemp ? <Trans>Remove temporary flag</Trans> : <Trans>Mark as temporary</Trans>}
			</MenuItem>
		</MenuGroup>
	);
});

export function buildVaultMobileMenuGroup(i18n: I18n, channel: Channel, closeAllSheets: () => void): MenuGroupType {
	const isHidden = Vault.isInHidden(channel.id);
	const isTemp = Vault.isTempChannel(channel.id);
	return {
		items: [
			{
				icon: <HideIcon size={20} data-flx="vault.mobile-menu-group.hide-icon" />,
				label: isHidden ? i18n._(UNHIDE_CONVERSATION_DESCRIPTOR) : i18n._(HIDE_CONVERSATION_DESCRIPTOR),
				onClick: () => {
					if (Vault.beginHideChannel(channel.id)) {
						openUnlockModal(VAULT_CODE_HIDDEN);
					}
					closeAllSheets();
				},
			},
			{
				icon: <RetryIcon size={20} data-flx="vault.mobile-menu-group.retry-icon" />,
				label: isTemp ? i18n._(REMOVE_TEMPORARY_DESCRIPTOR) : i18n._(MARK_AS_TEMPORARY_DESCRIPTOR),
				onClick: () => {
					if (Vault.beginTempMark(channel.id)) {
						openUnlockModal(VAULT_CODE_TEMP);
					}
					closeAllSheets();
				},
			},
		],
	};
}
